// Time of day in 15-minute cells (SPEC §3.1). Ranges are half-open [start, end); 96 = midnight (24:00).

export const MINUTES_PER_CELL: number = 15;
export const CELLS_PER_DAY: number = 96;
export const CELLS_PER_HOUR: number = 4;

export interface CellRange {
  start: number;
  end: number;
}

/** "HH:mm" for a cell boundary; 96 is shown as 24:00 (SPEC §3.1). */
export function formatCell(cell: number): string {
  const totalMinutes: number = cell * MINUTES_PER_CELL;
  const hours: number = Math.floor(totalMinutes / 60);
  const minutes: number = totalMinutes % 60;
  return `${padTwo(hours)}:${padTwo(minutes)}`;
}

/** "16:00–18:00". */
export function formatRange(range: CellRange): string {
  return `${formatCell(range.start)}–${formatCell(range.end)}`;
}

/** Length of a range in minutes. */
export function rangeMinutes(range: CellRange): number {
  return (range.end - range.start) * MINUTES_PER_CELL;
}

/** Hours and minutes of a length, e.g. 90 → { hours: 1, minutes: 30 }. */
export function splitMinutes(totalMinutes: number): { hours: number; minutes: number } {
  return { hours: Math.floor(totalMinutes / 60), minutes: totalMinutes % 60 };
}

/** True when 0 ≤ start < end ≤ 96. */
export function isValidRange(range: CellRange): boolean {
  return range.start >= 0 && range.end <= CELLS_PER_DAY && range.start < range.end;
}

/** True when the two ranges share at least one cell. */
export function rangesOverlap(first: CellRange, second: CellRange): boolean {
  return first.start < second.end && first.end > second.start;
}

/**
 * The end after the start changed (SPEC §9.4): it follows the start keeping the length, clamped to midnight.
 * The length is the current one, so a hand-set non-standard length is kept.
 */
export function followStart(previous: CellRange, newStart: number): CellRange {
  const lengthCells: number = previous.end - previous.start;
  const end: number = Math.min(newStart + lengthCells, CELLS_PER_DAY);
  return { start: newStart, end: Math.max(end, newStart + 1) };
}

/** A new range starting at the cell with the default length (SPEC §3.1: default 2 h). */
export function defaultRange(startCell: number, defaultLessonCells: number): CellRange {
  const end: number = Math.min(startCell + defaultLessonCells, CELLS_PER_DAY);
  return { start: startCell, end };
}

function padTwo(value: number): string {
  return value.toString().padStart(2, "0");
}
